import React from "react";
import PropTypes from "prop-types";
import produce from "immer";

import ResponsiveImageWithFallback from "../../atoms/responsive-image-with-fallback";
// import Carousel from "../../atoms/carousel";
import MultipleStoryFourCollectionStoryStack from "../../atoms/multiple-story-4c-story-stack";

import styles from "./styles.m.css";

import { Link } from "@quintype/components";
import { getStoryHeadline, getStoryData, generateImageSources } from "../../utils/utils";
import { isPremium } from "../../../../isomorphic/data/story";
import { getAuthorPath } from "../../../../isomorphic/data/author";

import Icon from "../../atoms/icon";
import Contributor from "../../atoms/contributor";
import TnElectionCommentsWidget from "../tn-election-commentlist-home";
import CollectionLink from "../../utils/generate-collection-link";

// const WrappedStoryStack = wrapCollectionLayout(MultipleStoryFourCollectionStoryStack);
const mobileStoryCount = 4;

const Election4CwithComments = ({ collection, metadata, accentColor, pageType, stories, className }) => {
  const { items = [] } = collection;
  // All collections
  const allCollections = items.filter(item => item.type === "collection");

  // First 4 are story collections
  const storyCollections = allCollections.slice(0, 4);
  // Last 2 are widgets (poll and comments)

  if (storyCollections.length < 1) {
    return null;
  }

  const [col1, col2, col3, col4] = storyCollections;

  // Helper to render a List Collection using WrappedStoryStack
  const renderListCollection = col => {
    if (!col || !col.items) return null;

    const filteredCol = produce(col, draft => {
      draft.items = (draft.items || [])
        .filter(({ story = {} }) => getStoryHeadline(story) != null && story.url != null)
        .slice(0, 5); // 1 Big + 4 Small
    });

    if (!filteredCol.items || filteredCol.items.length === 0) return null;

    return (
      <div className={styles["list-collection-wrapper"]}>
        <div className={styles["list-header"]}>
          <div className={styles["list-title"]}>
            <CollectionLink collection={col} disableAppSlugCheck={true} className={styles["collection-title"]}>
              <h2>{col.name}</h2>
              <span className={styles["green-arrow"]}>
                <Icon type="slider-next" />
              </span>
            </CollectionLink>
          </div>
        </div>

        <div className={styles["list-content"]}>
          <MultipleStoryFourCollectionStoryStack
            bigStory={true}
            mobileStoryCount={mobileStoryCount}
            stories={filteredCol.items.map(item => item.story)}
          />
        </div>
      </div>
    );
  };

  // Prepare 1st Collection: White Grid (6 stories in 2 columns)
  const whiteGridItems =
    col1 && col1.items ? col1.items.filter(({ story }) => story && story.headline).slice(0, 6) : [];

  const whiteGridCards = whiteGridItems.map(item => {
    const data = getStoryData(item.story);
    const isVideoStory = item.story && item.story["story-template"] === "video";
    return (
      <Link
        key={item.id}
        href={item.story.url}
        externalLink={`${item.story.url}`}
        className={styles["white-grid-card"]}
      >
        <div className={styles["white-card-image-wrapper"]}>
          <ResponsiveImageWithFallback
            slug={data.imageS3Key}
            metadata={data.imageMetadata}
            isPremium={isPremium(item.story)}
            alt={data.imageCaption}
            imgParams={{ auto: ["format", "compress"] }}
            sources={generateImageSources(
              { aspectRatio: [16, 9], screenWidthCoverage: 0.4 },
              { aspectRatio: [16, 9], screenWidthCoverage: 0.4 }
            )}
            story={item.story}
          />
          {isVideoStory && (
            <div className={styles["videoicon"]}>
              <Icon type="video-play" />
            </div>
          )}
        </div>
        <h3 className={styles["white-card-headline"]}>{data.headline}</h3>
      </Link>
    );
  });

  // Prepare 2nd Collection: Blue Slider
  const blueItems = col2 && col2.items ? col2.items.filter(({ story }) => story && story.headline).slice(0, 6) : [];

  const blueSlides = blueItems.map(item => {
    const data = getStoryData(item.story);
    const author = item.story.authors && item.story.authors[0] ? item.story.authors[0] : null;
    return (
      <Link key={item.id} href={item.story.url} externalLink={`${item.story.url}`} className={styles["blue-card"]}>
        <div className={styles["blue-card-image-wrapper"]}>
          <ResponsiveImageWithFallback
            className={styles["image-wrapper"]}
            slug={data.imageS3Key}
            metadata={data.imageMetadata}
            isPremium={isPremium(item.story)}
            alt={data.imageCaption}
            imgParams={{ auto: ["format", "compress"] }}
            sources={generateImageSources(
              { aspectRatio: [4, 3], screenWidthCoverage: 0.33 },
              { aspectRatio: [4, 3], screenWidthCoverage: 0.33 }
            )}
            story={item.story}
          />
          <div className={styles["blue-card-overlay"]}>
            <h3 className={styles["blue-card-headline"]}>{data.headline}</h3>
            {author && (
              <Contributor
                name={author.name}
                type={author["contributor-role"] && author["contributor-role"].name}
                path={getAuthorPath(author)}
                className={styles["blue-card-author"]}
              />
            )}
          </div>
        </div>
      </Link>
    );
  });

  return (
    <div className={styles["base"]}>
      <div className={`${styles["container-story"]} container`}>
        <div className={styles["main-content-grid"]}>
          {/* Left Column: All 4 Story Collections */}
          <div className={styles["left-content-column"]}>
            {/* 1st Collection: White Grid (2 columns x 3 rows) */}
            {col1 &&
              whiteGridCards.length > 0 && (
                <div className={styles["white-grid-wrapper"]}>
                  <div className={styles["white-grid-header"]}>
                    <div className={styles["white-grid-title"]}>
                      <h2>{col1.name}</h2>
                      <CollectionLink collection={col1} disableAppSlugCheck={true}>
                        <span className={styles["green-arrow"]}>
                          <Icon type="slider-next" />
                        </span>
                      </CollectionLink>
                    </div>
                  </div>
                  <div className={styles["white-grid-content"]}>
                    <div className={styles["white-cards-grid"]}>{whiteGridCards}</div>
                  </div>
                </div>
              )}

            {/* 2nd Collection: Blue Slider */}
            {col2 &&
              blueSlides.length > 0 && (
                <div className={styles["blue-collection-wrapper"]}>
                  <div className={styles["blue-collection-header"]}>
                    <div className={styles["header-line"]} />
                    <h2 className={styles["blue-collection-title"]}>
                      <CollectionLink collection={col2} disableAppSlugCheck={true}>
                        {col2.name}
                      </CollectionLink>
                    </h2>
                    <div className={styles["header-line"]} />
                  </div>
                  <div className={styles["blue-slider-custom"]}>
                    <div className={styles["blue-slider-track"]}>{blueSlides}</div>
                  </div>
                </div>
              )}

            {/* 3rd & 4th Collections: Full-width Stacks */}
            <div className={styles["list-collections-stack"]}>
              {renderListCollection(col3)}
              {renderListCollection(col4)}
            </div>
          </div>

          {/* Right Column: Widgets */}
          <div className={styles["right-content-column"]}>
            {/* Widget 2: Comments */}
            <div className={styles["manifest-wrapper"]}>
              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLScA9cvhWoGra2XKSxtjR7IZRsnCEOl21beMOMwArKi9yhMO8Q/viewform"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="https://media.vikatan.com/vikatan/2026-03-03/qifb682k/Manifestobannermobile.png" alt="" />
              </a>
            </div>
            <div>
              <TnElectionCommentsWidget />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Election4CwithComments.propTypes = {
  accentColor: PropTypes.string,
  collection: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.number,
    slug: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.object)
  }),
  metadata: PropTypes.shape({
    read_more_text: PropTypes.string
  }),
  pageType: PropTypes.string,
  stories: PropTypes.arrayOf(PropTypes.object),
  className: PropTypes.string
};

export default Election4CwithComments;
